import { useState, useEffect } from "react";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileText, FolderOpen, BookOpen, X } from "lucide-react";
import { SecurePdfViewer } from "@/components/SecurePdfViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StudyMaterials = () => {
  const { t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  
  // PDF Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("aestha_token");
      const [catRes, matRes] = await Promise.all([
        axios.get(`${API}/study-categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/study-materials`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCategories(catRes.data);
      setMaterials(matRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const openPdfViewer = async (material) => {
    setSelectedMaterial(material);
    setViewerOpen(true);
    setLoadingPdf(true);
    
    try {
      const token = localStorage.getItem("aestha_token");
      const response = await axios.get(`${API}/study-materials/${material.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPdfData(response.data.file_data);
    } catch (error) {
      console.error("Error loading PDF:", error);
    } finally {
      setLoadingPdf(false);
    }
  };

  const closePdfViewer = () => {
    setViewerOpen(false);
    setSelectedMaterial(null);
    setPdfData(null);
  };

  const filteredMaterials = selectedCategory === "all" 
    ? materials 
    : materials.filter(m => m.category_id === selectedCategory);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <Layout>
      <div className="p-6 md:p-12" data-testid="study-materials-page">
        {/* Header */}
        <div className="mb-8">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t("study_materials")}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl">{t("materials")}</h1>
        </div>

        {/* Filter */}
        <div className="mb-8">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full md:w-64">
              <SelectValue placeholder={t("select_category")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_categories")}</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Materials Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-40 loading-skeleton" />
            ))}
          </div>
        ) : filteredMaterials.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMaterials.map((material) => (
              <div
                key={material.id}
                className="bento-item cursor-pointer hover:border-foreground transition-colors"
                onClick={() => openPdfViewer(material)}
                data-testid={`material-card-${material.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border flex items-center justify-center bg-secondary shrink-0">
                    <FileText size={24} strokeWidth={1} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg truncate">{material.title}</h3>
                    {material.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                        {material.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                      <FolderOpen size={12} />
                      <span>{material.category_name}</span>
                      <span>•</span>
                      <span>{formatFileSize(material.file_size)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <BookOpen className="empty-state-icon" />
            <p className="empty-state-title">{t("no_materials")}</p>
            <p className="empty-state-description">
              {t("no_materials_message")}
            </p>
          </div>
        )}

        {/* PDF Viewer Dialog */}
        <Dialog open={viewerOpen} onOpenChange={closePdfViewer}>
          <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden" aria-describedby="pdf-viewer-description">
            <span id="pdf-viewer-description" className="sr-only">
              Visualizador de PDF seguro. Use os controles para navegar e ajustar o zoom.
            </span>
            <DialogHeader className="p-4 border-b">
              <div className="flex items-center justify-between">
                <DialogTitle className="font-serif text-xl">
                  {selectedMaterial?.title}
                </DialogTitle>
                <Button variant="ghost" size="sm" onClick={closePdfViewer}>
                  <X size={20} />
                </Button>
              </div>
            </DialogHeader>
            
            <div 
              className="flex-1 overflow-hidden"
              style={{
                height: "calc(90vh - 80px)",
              }}
            >
              {loadingPdf ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground"></div>
                </div>
              ) : (
                <SecurePdfViewer 
                  pdfData={pdfData} 
                  title={selectedMaterial?.title} 
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StudyMaterials;
